import { AlertTriangle, ShieldAlert, Lightbulb } from 'lucide-react'

export type StepSeverity = 'tip' | 'caution' | 'danger'

const STYLES: Record<
  StepSeverity,
  { label: string; Icon: typeof AlertTriangle; wrap: string; icon: string; title: string }
> = {
  tip: {
    label: 'Tip',
    Icon: Lightbulb,
    wrap: 'border-sky-300 bg-sky-50 dark:border-sky-900/50 dark:bg-sky-950/30',
    icon: 'text-sky-600 dark:text-sky-400',
    title: 'text-sky-800 dark:text-sky-200',
  },
  caution: {
    label: 'Caution',
    Icon: AlertTriangle,
    wrap: 'border-amber-300 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30',
    icon: 'text-amber-600 dark:text-amber-400',
    title: 'text-amber-800 dark:text-amber-200',
  },
  danger: {
    label: "Don't do this",
    Icon: ShieldAlert,
    wrap: 'border-red-300 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30',
    icon: 'text-red-600 dark:text-red-400',
    title: 'text-red-800 dark:text-red-200',
  },
}

/**
 * Public-facing severity callout for tutorial / CAS steps. Wraps a single step
 * (caution/tip) or a grouped run of "don't do this" steps (danger) in a colored
 * panel with an icon + text label, so a warning never relies on color alone
 * (colorblind / screen-reader safe). Severity-driven (tip/caution/danger),
 * deliberately separate from the admin-docs Callout's kind vocabulary.
 */
export function StepCallout({
  severity,
  children,
}: {
  severity: StepSeverity
  children: React.ReactNode
}) {
  const s = STYLES[severity]
  const Icon = s.Icon
  return (
    <div className={`rounded-sm border p-4 ${s.wrap}`} role="note" aria-label={s.label}>
      <div
        className={`mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wide ${s.title}`}
      >
        <Icon className={`h-4 w-4 shrink-0 ${s.icon}`} aria-hidden />
        <span>{s.label}</span>
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  )
}
